import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Flame, TrendingUp, Sparkles, Trophy, Award, Lock } from "lucide-react";
import { motion } from "framer-motion";

/**
 * GamificationHero - top dashboard section with 4 stat cards + achievements panel.
 * Calls GET /me/gamification for streak/xp/rank/completion + earned/locked achievements.
 */
export const GamificationHero = ({ apiUrl, studentName, lang = "ms" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isEn = lang === "en";

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/me/gamification`);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-[#F1F5F9] p-5 h-28 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const stats = [
    {
      key: "streak",
      icon: Flame,
      value: data.streak || 0,
      label: isEn ? "Day Streak" : "Hari Streak",
      iconBg: "bg-orange-50",
      iconColor: "text-orange-500",
      filled: data.streak > 0,
    },
    {
      key: "completion",
      icon: TrendingUp,
      value: `${data.completion || 0}%`,
      label: isEn ? "Completion" : "Kemajuan",
      iconBg: "bg-blue-50",
      iconColor: "text-[#2563EB]",
    },
    {
      key: "xp",
      icon: Sparkles,
      value: (data.xp || 0).toLocaleString(),
      label: isEn ? "XP Points" : "Mata XP",
      iconBg: "bg-emerald-50",
      iconColor: "text-emerald-500",
    },
    {
      key: "rank",
      icon: Trophy,
      value: data.rank ? `#${data.rank}` : "-",
      label: isEn ? "Rank" : "Kedudukan",
      iconBg: "bg-blue-50",
      iconColor: "text-[#2563EB]",
    },
  ];

  return (
    <div className="space-y-6 mb-8" data-testid="gamification-hero">
      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stat-cards">
        {stats.map((s, idx) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: idx * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-[#F1F5F9] p-4 sm:p-5 hover:shadow-md transition-shadow"
              data-testid={`stat-card-${s.key}`}
            >
              <div className={`w-10 h-10 rounded-full ${s.iconBg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={s.iconColor} fill={s.filled ? "currentColor" : "none"} />
              </div>
              <div className="text-2xl font-bold text-[#0F172A] leading-none tabular-nums" style={{ fontFamily: "Inter, sans-serif" }}>
                {s.value}
              </div>
              <div className="text-xs text-[#64748B] mt-1.5 font-medium">{s.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Achievement panel */}
      {data.achievements && (data.achievements.earned_count > 0 || data.achievements.locked.length > 0) && (
        <div className="bg-white rounded-2xl shadow-sm border border-[#F1F5F9] p-5" data-testid="achievements-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-bold text-[#0F172A] flex items-center gap-2 uppercase tracking-widest">
              <Award size={14} className="text-emerald-500" />
              {isEn ? "Recent Achievements" : "Pencapaian Terkini"}
            </h3>
            <span className="text-xs text-[#64748B] font-semibold tabular-nums">
              {data.achievements.earned_count}/{data.achievements.total}
            </span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {/* Earned first (capped to 5) */}
            {(data.achievements.earned || []).slice(0, 5).map((a) => (
              <AchievementTile key={a.id} ach={a} earned={true} lang={lang} />
            ))}
            {/* Then locked (preview next ones) */}
            {(data.achievements.locked || []).slice(0, Math.max(0, 5 - data.achievements.earned_count)).map((a) => (
              <AchievementTile key={a.id} ach={a} earned={false} lang={lang} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AchievementTile = ({ ach, earned, lang }) => {
  const isEn = lang === "en";
  return (
    <div
      className={`relative flex flex-col items-center text-center p-3 rounded-xl transition-all ${
        earned
          ? "bg-amber-50/60 ring-1 ring-amber-100"
          : "bg-[#F8FAFC] opacity-60"
      }`}
      title={earned ? `+${ach.xp} XP` : (isEn ? "Locked" : "Belum dibuka")}
      data-testid={`achievement-${ach.id}`}
    >
      <div className={`text-2xl mb-1 ${earned ? "" : "grayscale"}`}>{ach.icon}</div>
      <div className={`text-[10px] font-semibold leading-tight line-clamp-2 ${earned ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
        {isEn ? ach.title_en : ach.title}
      </div>
      {!earned && (
        <Lock size={9} className="absolute top-1.5 right-1.5 text-[#94A3B8]" />
      )}
    </div>
  );
};

export default GamificationHero;
